// Art assignment (face templates spec section 3). Pure: maps an object's kind, tags and catalog key
// to an atlas frame (or a pattern) per art slot. A missing frame or sheet falls back and is
// reported, never thrown, so art can never block play.

import type { ArtFile, ArtRule, AtlasesFile, AtlasFrame } from './types.ts';

export interface SheetInfo { url: string; width: number; height: number }

export type ArtValue =
  | { kind: 'frame'; ref: string; url: string; sheet: { w: number; h: number }; frame: { x: number; y: number; w: number; h: number };
      /** art.json focus for this slot (overrides the layer), and the frame pivot (the default). */
      focus: [number, number] | null; pivot: [number, number] }
  | { kind: 'pattern'; pattern: string };

/** `fellBack`: the slot used art.json's fallback. `missing`: an assigned frame or sheet that does not exist. */
export interface ArtResult { value: ArtValue | null; fellBack: boolean; missing: string | null }

export interface ArtTarget { kind: string; catalogKey: string | null; tags: string[]; faceDown: boolean }

export type ArtResolver = (target: ArtTarget, slot: string) => ArtResult;

export const ART_PATTERNS = ['stripes', 'dots', 'grid', 'crosshatch', 'noise', 'wood', 'linen'] as const;

/** Every frame of every atlas as `atlasId/frame`, with the sheet asset it lives on. */
export function frameIndex(atlases: AtlasesFile | null): Map<string, { asset: string; frame: AtlasFrame }> {
  const out = new Map<string, { asset: string; frame: AtlasFrame }>();
  for (const a of atlases?.atlases ?? []) {
    for (const [name, frame] of Object.entries(a.frames ?? {})) out.set(`${a.id}/${name}`, { asset: a.asset, frame });
    if (a.grid && a.names) {
      const cw = typeof a.grid.cell === 'number' ? a.grid.cell : a.grid.cell.w, ch = typeof a.grid.cell === 'number' ? a.grid.cell : a.grid.cell.h;
      a.names.forEach((name, i) => out.set(`${a.id}/${name}`, { asset: a.asset, frame: { x: (i % a.grid!.columns) * cw, y: Math.floor(i / a.grid!.columns) * ch, w: cw, h: ch } }));
    }
  }
  return out;
}

const specificity = (r: ArtRule) => (r.match.catalog ? 3 : r.match.tags ? 2 : r.match.kind ? 1 : 0);

function matches(r: ArtRule, t: ArtTarget): boolean {
  const m = r.match;
  if (m.kind && m.kind !== t.kind) return false;
  if (m.tags && !m.tags.every(tag => t.tags.includes(tag))) return false;
  if (m.catalog && (!t.catalogKey || !m.catalog.includes(t.catalogKey))) return false;
  return true;
}

export function createArtResolver(atlases: AtlasesFile | null, art: ArtFile | null, sheet: (assetId: string) => SheetInfo | null, asset = sheet): ArtResolver {
  const frames = frameIndex(atlases);
  // Least to most specific, file order within a level; later rules override per slot.
  const rules = (art?.assign ?? []).map((r, i) => ({ r, i })).sort((a, b) => specificity(a.r) - specificity(b.r) || a.i - b.i).map(x => x.r);
  const cache = new Map<string, ArtResult>();
  const decode = (value: string, focus: [number, number] | undefined): { value: ArtValue | null; missing: string | null } => {
    if (value === 'none') return { value: null, missing: null };
    if (value.startsWith('pattern:')) return { value: { kind: 'pattern', pattern: value.slice(8) }, missing: null };
    if (value.startsWith('asset:')) {
      const s = asset(value.slice(6));
      return s ? { value: { kind: 'frame', ref: value, url: s.url, sheet: { w: s.width, h: s.height }, frame: { x: 0, y: 0, w: s.width, h: s.height }, focus: focus ?? null, pivot: [50, 50] }, missing: null } : { value: null, missing: value };
    }
    const f = frames.get(value);
    const s = f ? sheet(f.asset) : null;
    if (!f || !s) return { value: null, missing: value };
    const pivot = f.frame.pivot ? [f.frame.pivot[0] * 100, f.frame.pivot[1] * 100] as [number, number] : [50, 50] as [number, number];
    return { value: { kind: 'frame', ref: value, url: s.url, sheet: { w: s.width, h: s.height }, frame: { x: f.frame.x, y: f.frame.y, w: f.frame.w, h: f.frame.h }, focus: focus ?? null, pivot }, missing: null };
  };
  return (t, slot) => {
    if (!art || !art.slots.includes(slot) || t.faceDown) return { value: null, fellBack: false, missing: null };
    const key = `${t.kind}|${t.catalogKey}|${t.tags.join(',')}|${slot}`;
    const hit = cache.get(key);
    if (hit) return hit;
    let assigned: string | undefined, focus: [number, number] | undefined;
    for (const r of rules) {
      if (!matches(r, t)) continue;
      if (typeof r[slot] === 'string') assigned = r[slot] as string;
      if (r.focus?.[slot]) focus = r.focus[slot];
    }
    let result: ArtResult;
    const primary = assigned !== undefined ? decode(assigned, focus) : null;
    if (primary && (primary.value || !primary.missing)) result = { value: primary.value, fellBack: false, missing: null };
    else {
      const fb = art.fallback?.[slot];
      const fallback = fb !== undefined ? decode(fb, undefined) : null;
      result = { value: fallback?.value ?? null, fellBack: true, missing: primary?.missing ?? fallback?.missing ?? null };
    }
    cache.set(key, result);
    return result;
  };
}
